import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProgressBarModule } from 'primeng/progressbar';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { JobService, JobStatus } from '../../../services/job.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-lead-distribution-status',
  templateUrl: './lead-distribution-status.component.html',
  styleUrls: ['./lead-distribution-status.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ProgressBarModule,
    CardModule,
    ButtonModule,
    TagModule
  ]
})
export class LeadDistributionStatusComponent implements OnInit, OnDestroy {
  @Input() jobId!: string;
  @Input() pollInterval = 5000;
  @Output() jobCompleted = new EventEmitter<JobStatus>();
  @Output() jobFailed = new EventEmitter<JobStatus>();
  @Output() jobUpdated = new EventEmitter<JobStatus>();

  job: JobStatus | null = null;
  loading = true;
  error: string | null = null;
  private subscription?: Subscription;

  constructor(
    private jobService: JobService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.jobId) {
      this.startPolling();
    }
  }

  ngOnDestroy(): void {
    console.log('LeadDistributionStatusComponent destroying, stopping polling for job:', this.jobId);
    this.stopPolling();
  }

  private startPolling(): void {
    this.loading = true;
    this.error = null;
    
    this.subscription = this.jobService.pollJobStatus(this.jobId, this.pollInterval)
      .subscribe({
        next: (job) => {
          console.log('LeadDistributionStatusComponent received job update:', job);
          
          // Ensure completed jobs show 100% progress
          if (job.status === 'completed' && job.progress < 100) {
            job.progress = 100;
          }
          
          this.job = job;
          this.loading = false;
          this.error = null;

          console.log('Job status update:', job);
          console.log('Progress bar will show:', job.progress, '%');

          // Force change detection to update UI
          this.cdr.markForCheck();
          
          // Emit progress updates for parent component
          this.jobUpdated.emit(job);
          
          // Emit events based on job status
          if (job.status === 'completed') {
            console.log('✅ Job completed - emitting jobCompleted event');
            this.stopPolling(); // Ensure polling stops
            this.jobCompleted.emit(job);
            console.log('✅ jobCompleted.emit() called');
          } else if (job.status === 'failed') {
            console.log('❌ Job failed - emitting jobFailed event');
            this.stopPolling(); // Ensure polling stops
            this.jobFailed.emit(job);
            console.log('❌ jobFailed.emit() called');
          } else {
            console.log('⏳ Job in progress, status:', job.status, 'progress:', job.progress);
          }
        },
        error: (error) => {
          this.loading = false;
          this.error = error.message || 'Failed to load job status';
          console.error('Job polling error:', error);
          
          // Create a failed job status and emit it
          const failedJob: JobStatus = {
            jobId: this.jobId,
            type: 'lead_distribution',
            status: 'failed',
            progress: 0,
            message: 'Error occurred while monitoring job status',
            error: this.error || undefined,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          this.job = failedJob;
          this.jobFailed.emit(failedJob);
        },
        complete: () => {
          console.log('Job polling completed for:', this.jobId);
          this.loading = false;
        }
      });
  }

  private stopPolling(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.jobService.stopPolling(this.jobId);
  }

  getStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'danger';
      case 'processing': return 'info';
      case 'queued': return 'warn';
      default: return 'info';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'completed': return 'pi pi-check-circle';
      case 'failed': return 'pi pi-times-circle';
      case 'processing': return 'pi pi-spin pi-spinner';
      case 'queued': return 'pi pi-clock';
      default: return 'pi pi-question-circle';
    }
  }

  retry(): void {
    if (this.job?.status === 'failed') {
      this.startPolling();
    }
  }

  getDisplayProgress(): number {
    if (!this.job) return 0;
    // If job is completed but progress is less than 100, return 100
    if (this.job.status === 'completed' && this.job.progress < 100) {
      return 100;
    }
    return this.job.progress || 0;
  }
}